import _ from 'lodash'
import Backbone from 'backbone'
import alertMetadata, { scoreComparisonOptionHtml } from 'scoreboard/alerts/alert-metadata'
import formHelper from 'scoreboard/components/form-helper'
import i18n from 'scoreboard/components/i18n'
import MarkedField from 'scoreboard/components/marked-field'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import numericValidator from 'scoreboard/components/validators/numeric-validator'
import numericRangeValidatorFactory from 'scoreboard/components/validators/numeric-range-validator-factory'
import numericEvaluator from 'scoreboard/components/evaluators/numeric-evaluator'
import select from 'scoreboard-select'
import config from 'scoreboard/components/config'
import template from './alert-subscription.hbs'
import * as GetCorrectSubscriptionValue from './get-correct-subscription-value'
const { getCorrectSubscriptionValue } = GetCorrectSubscriptionValue
import customTextThresholds from './custom-text-thresholds.hbs'
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO } = DataType

/**
 * Alert subscription details (editable)
 * Screenshot: https://github.com/SpiderStrategies/Scoreboard/issues/21929#issuecomment-457752677
 */
const AlertSubscription = Backbone.View.extend({

	className: 'panel-content alert-settings',

	/**
	 *
	 * @param {Object} options.subscription
	 * @param {Object} options.scorecardItem
	 * @param {String[]} options.buttons example: ['delete'] will render a delete button
	 * using the standard covert right button layout.  Useful when multiple
	 * subscriptions are in a list view.
	 */
	initialize(options = {}) {
		this.options = options
		const {subscription, scorecardItem} = options
		this.subscription = subscription || {}
		this.scorecardItem = scorecardItem || {}
	},

	render: function() {
		const {id, type} = this.subscription

		this.$el.empty().append(template({
			subscription: this.subscription,
			text: i18n.get(alertMetadata.sendAlertWhenKeys[type]),
			buttons: this.options.buttons
		}))
		this.$el.attr('data-id', id)

		this.$('.score-comparison').append(scoreComparisonOptionHtml())

		// Remove the options that should only be present for score alerts
		if (type !== 'score-changed') {
			this.$('.settings-text:last, [name=descendants]').remove()
		}
		if (type === 'comment-added') {
			this.$('.subscription-control-wrapper:first-child').addClass('notes-subscription')
			this.$('select, input').remove()
		}

		const {dataType, currencyCode} = this.scorecardItem

		const threshold = this.subscription.threshold

		if (type === 'metric-value-changed') {
			let customTextScoringType = config.customTextScoringType(this.scorecardItem)
			this.subscription.threshold = getCorrectSubscriptionValue(threshold, dataType, customTextScoringType)
		}

		// issue #6448: write the value BEFORE rendering the numeric-threshold
		// field so that the decimal is moved appropriately
		formHelper.writeToDom(this.subscription, this.el)

		if (this.$('.numeric-threshold').length > 0) {
			var validators = [requiredValidator, numericValidator]
			const thresholdMarkingOptions = {
				el: this.$('.numeric-threshold'),
				validators,
				evaluator: numericEvaluator,
				dataType,
				currencyCode,
				resizeWithText: true
			}
			// scores must be in configured range (#47859 taking into account
			// if scores are allowed to extend beyond the min/max)
			if (type === 'score-changed') {
				_.extend(thresholdMarkingOptions, {
					dataType: 'score',
					validators: [...validators, this.createNumericRangeValidator()]
				})
			}
			this.thresholdField = new MarkedField(thresholdMarkingOptions).render()
			// Make sure the input is resized for larger entries #38170
			process.nextTick(() => this.thresholdField.resize())
		}

		if (this.subscription) {
			this.updateDataType(this.scorecardItem)
			// #39484 only handle changing dataTypes for "metric value changed"
			// alert subscriptions, not needed for "score changed" alerts
			if (type === 'metric-value-changed') {
				let customText = config.customTextScoringType(this.scorecardItem)
				if (dataType === YES_NO || customText) {
					// if the user switches from a yes/no metric or a custom text
					// scoring type to a metric with a different type, don't
					// pre-populate a 1 or 0 in the numeric threshold input
					this.$('.numeric-threshold').val('')
				} else {
					// similarly if switching TO yes/no or a custom text scoring
					// type, make sure the first option is selected
					this.$('select.yes-no-threshold').prop('selectedIndex', 0).change()
					this.$('select.custom-text-threshold').prop('selectedIndex', 0).change()
				}
			}
		}

		select(this.el.querySelectorAll('select'))

		this._toggleMissingValueBanner()

		return this
	},

	/**
	 * Creates numeric range validator for score values
	 */
	createNumericRangeValidator: function() {
		let {
			minimumScore, maximumScore, allowScoresAboveMax,
			allowScoresBelowMin
		} = config

		let lowValue = allowScoresBelowMin ? null : minimumScore
		let highValue = allowScoresAboveMax ? null : maximumScore

		let rangeOpts = {
			excludeHigh: allowScoresAboveMax,
			excludeLow: allowScoresBelowMin
		}
		let formattingOpts = {
			dataType: 'score'
		}
		return numericRangeValidatorFactory(lowValue, highValue,
			formattingOpts, rangeOpts)
	},

	_toggleMissingValueBanner: function() {
		this.$('.row-error').toggleClass('is-not-displayed', true)

		const dataType = this.scorecardItem.dataType
		if (dataType !== YES_NO) return

		if (this.subscription.type !== 'metric-value-changed') return

		const threshold = this.subscription.threshold
		if (!_.isNil(threshold) && ![ 1, 0, '1', '0', 'null' ].includes(threshold)) {
			// #38354 if this is a yes-no metric but the threshold is not a valid
			// yes/no value, show a banner telling the user to update the value
			this.$('.row-error').toggleClass('is-not-displayed', false)
			// Select the first option by default
			this.$('select.yes-no-threshold').prop('selectedIndex', 0).change()
		}
	},

	updateDataType: function(node) {
		const {type} = this.subscription

		var yesNo = node && node.dataType === YES_NO
		let customText = config.customTextScoringType(node)
		let yesNoOrCustom = yesNo || customText
		if (type === 'metric-value-changed') {
			// toggle the numeric and yes/no threshold fields based on whether
			// the node is yes/no
			this.thresholdField.toggle(!yesNoOrCustom)
			this.$('.yes-no-threshold').toggleClass('is-not-displayed', !yesNo)
			// toggle the comparison field based on whether the node is yes/no
			// or uses a custom text scoring type
			this.$('.score-comparison').toggleClass('is-not-displayed', yesNoOrCustom)
			// display a select for custom text thresholds
			this.renderCustomTextSelect(node)
			this.$('.custom-text-threshold').toggleClass('is-not-displayed', !customText)
		}
		if (!yesNoOrCustom && type === 'metric-value-changed') {
			_.extend(this.thresholdField.options, {
				dataType: node.dataType,
				// important: be sure to set this b/c nodes without currency
				// codes will just have this omitted
				currencyCode: node.currencyCode
			})
			this.thresholdField.mark()
		}
	},

	/**
	 * Renders a dropdown of the thresholds for items with a custom text scoring
	 * type
	 */
	renderCustomTextSelect: function(item) {
		let scoringType = config.scorecardItemScoringType(item)
		let customThresholds = customTextThresholds(scoringType)
		this.$('.custom-text-threshold-container').empty().html(customThresholds)
		select(this.el.querySelectorAll('select.custom-text-threshold'))

		let thresholds = _.get(scoringType, 'thresholds', [])
		let thresholdIds = _.map(thresholds, 'id')
		// set the value if we have a valid threshold value
		// compare threshold using _.toInteger because we might have "101" or
		// 101 depending on the section)
		let threshold = _.get(this.subscription, 'threshold')
		if (!_.isNil(threshold) && _.includes(thresholdIds, _.toInteger(threshold))) {
			this.$('select.custom-text-threshold').val(threshold)
		} else {
			// otherwise, select the first option ("No Value")
			this.$('select.custom-text-threshold').prop('selectedIndex', 0).change()
		}
	},

	/**
	 * Extract form values into this.options.subscription
	 * @returns the updated subscription object
	 */
	updateSubscriptionWithFormValues() {
		return _.extend({
			comparison: 'equals' // #38187 Comparison operator is required
		}, this.subscription, formHelper.readFromDom(this.el))
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		_.invokeMap([this.thresholdField], 'remove')
	}

})

export default AlertSubscription

AlertSubscription.__filename__ = 'alerts/alert-subscription.js'
